const PaymentSummary = ({ order }) => {
  const totalItems = order.orderItems.reduce((sum, i) => sum + i.qty, 0);

  const paymentStatus = order.payments?.paymentStatus;

  const isPaid = paymentStatus === "paid";
  const isRefunded = paymentStatus === "refunded";

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden text-sm">
      {/* HEADER */}
      <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center gap-3">
          {/* Status Icon */}
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center ${
              isRefunded
                ? "bg-red-100 text-red-600"
                : isPaid
                  ? "bg-green-100 text-green-600"
                  : "bg-yellow-100 text-yellow-600"
            }`}
          >
            {isRefunded ? "↺" : isPaid ? "✓" : "•"}
          </div>

          {/* Title */}
          <div>
            <p className="font-semibold text-navy">Payment Summary</p>

            <p className="text-xs text-gray-500">
              {isRefunded
                ? "Refund completed"
                : isPaid
                  ? "Payment successful"
                  : "Awaiting payment"}
            </p>
          </div>
        </div>

        {/* Badge */}
        <div
          className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${
            isRefunded
              ? "bg-red-100 text-red-600"
              : isPaid
                ? "bg-green-100 text-green-600"
                : "bg-yellow-100 text-yellow-700"
          }`}
        >
          {paymentStatus || "pending"}
        </div>
      </div>

      {/* BODY */}
      <div className="p-5 space-y-4">
        {/* ITEMS */}
        <div className="flex items-center justify-between text-gray-600">
          <span>Total Items</span>
          <span className="font-medium">{totalItems}</span>
        </div>

        {/* SUBTOTAL */}
        <div className="flex justify-between">
          <span className="text-gray-500">Subtotal</span>

          <span className="font-medium">${order.subTotalPrice.toFixed(2)}</span>
        </div>

        {/* GST */}
        <div className="flex justify-between">
          <span className="text-gray-500">GST 15%</span>

          <span className="font-medium">${order.taxprice.toFixed(2)}</span>
        </div>

        {/* PICKUP */}
        <div className="rounded-2xl bg-gray-50 border border-gray-100 p-4">
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
            Pickup Information
          </p>

          <p className="font-medium text-gray-700">{order.operator?.name}</p>

          <p className="text-xs text-gray-500 mt-1">
            0.0 kg • Items 0.0 kg • Package 0.0 kg
          </p>
        </div>

        {/* TOTAL */}
        <div className="border-t pt-4 flex justify-between items-center">
          <div>
            <p className="font-semibold text-base text-navy">Total</p>

            <p className="text-xs text-gray-400 mt-1">Including GST</p>
          </div>

          <span className="text-2xl font-bold text-navy">
            ${order.totalPrice.toFixed(2)}
          </span>
        </div>

        {/* PAYMENT INFO */}
        {order.payments && (
          <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 overflow-hidden">
            {/* Top */}
            <div className="px-4 py-3 border-b border-gray-100 bg-white">
              <h3 className="font-semibold text-navy">Payment Details</h3>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3">
              {/* Method */}
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Payment Method</span>

                <span className="font-medium capitalize">
                  {order.payments?.paymentMethod || "-"}
                </span>
              </div>

              {/* Session */}
              {order.payments?.sessionId && (
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500 shrink-0">Session ID</span>

                  <span className="font-mono text-xs text-right break-all text-gray-700">
                    {order.payments?.sessionId}
                  </span>
                </div>
              )}

              {/* Payment Intent */}
              {order.payments?.paymentIntentId && (
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500 shrink-0">Payment Intent</span>

                  <span className="font-mono text-xs text-right break-all text-gray-700">
                    {order.payments?.paymentIntentId}
                  </span>
                </div>
              )}

              {/* REFUND SECTION */}
              {isRefunded && (
                <>
                  <div className="border-t border-dashed pt-3" />

                  <div className="rounded-2xl border border-red-100 bg-red-50 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-red-700">
                          Refund Completed
                        </p>

                        <p className="text-xs text-red-500 mt-1">
                          Payment returned to customer
                        </p>
                      </div>

                      <div className="px-2.5 py-1 rounded-full bg-red-100 text-red-600 text-xs font-bold uppercase">
                        Refunded
                      </div>
                    </div>

                    {/* Refund Amount */}
                    <div className="flex justify-between">
                      <span className="text-red-500">Refund Amount</span>

                      <span className="font-bold text-red-600">
                        ${order.payments?.refundAmount}
                      </span>
                    </div>

                    {/* Refund ID */}
                    {order.payments?.refundId && (
                      <div className="flex justify-between gap-4">
                        <span className="text-red-500 shrink-0">Refund ID</span>

                        <span className="font-mono text-xs text-right break-all text-red-700">
                          {order.payments?.refundId}
                        </span>
                      </div>
                    )}

                    {/* Refund Date */}
                    <div className="flex justify-between">
                      <span className="text-red-500">Refund Date</span>

                      <span className="text-red-700 font-medium">
                        {new Date(order.payments?.paymentDate).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSummary;

// const PaymentSummary = ({ order }) => {
//   const totalItems = order.orderItems.reduce((sum, i) => sum + i.qty, 0);

//   const isPaid = order.payments?.paymentStatus === "paid";

//   return (
//     <div className="bg-white rounded-xl shadow text-sm">
//       {/* HEADER */}
//       <div className="flex items-center justify-between px-4 py-3 border-b">
//         <div className="flex items-center gap-2">
//           <span className="text-green-600 text-sm">✓</span>
//           <span className="font-medium">{isPaid ? "Paid" : "Pending"}</span>
//         </div>

//         <span className="text-gray-400">•••</span>
//       </div>

//       {/* BODY */}
//       <div className="p-4 space-y-3">
//         {/* ITEMS COUNT */}
//         <div className="text-gray-600 text-sm">{totalItems} items</div>

//         {/* SUBTOTAL */}
//         <div className="flex justify-between">
//           <span>Subtotal</span>
//           <span>${order.subTotalPrice.toFixed(2)}</span>
//         </div>

//         {/* PICKUP INFO */}
//         <div className="text-gray-500 text-xs leading-5">
//           Pickup <br />
//           {order.operator?.name} (0.0 kg: Items 0.0 kg, Package 0.0 kg)
//         </div>

//         {/* TAX */}
//         <div className="flex justify-between">
//           <span>GST 15%</span>
//           <span>${order.taxprice.toFixed(2)}</span>
//         </div>

//         {/* TOTAL */}
//         <div className="border-t pt-3 flex justify-between font-semibold text-base">
//           <span>Total</span>
//           <span>${order.totalPrice.toFixed(2)}</span>
//         </div>

//         {/* PAID AGAIN (BOTTOM) */}
//         <div className="flex justify-between text-gray-700">
//           <span>{isPaid ? "Paid" : "Pending"}</span>
//           <span>${order.totalPrice.toFixed(2)}</span>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default PaymentSummary;
